/**
 * The robot copy table (twin slice 1, plan W5 step 6; ideation §3c).
 *
 * One row per thing a robot can report, each with the hotel's words, who
 * acts and how urgent it is. The rows cover the Phantas adapter's full set
 * of issue categories (`tests/fixtures/contracts/phantas-issue-categories
 * .json`, held to the adapter by a drift test and to this table by a
 * bijection test), the reasons a robot is unavailable, and three derived
 * conditions: a bare `robot_error`, a stuck task with no named issue, and
 * a robot waiting to be re-localised.
 *
 * WORDS ARE THE HOTEL'S. No codes, no "RMF", no metres -- a test reads
 * every row for a site audience. Ops read the same words (one reading, two
 * audiences); their extra detail is the raw category, carried separately.
 * Anything the table does not know falls back to the robot's own text,
 * quoted, for OPS -- a guess at what an unknown category means would be
 * worse than the adapter's sentence -- and to `NEEDS_A_CHECK` for a site
 * audience, which never reads the raw text (it is often a code).
 *
 * COPY STATUS: pending Akash's copy pass 2 (plan gate B).
 */
import { levelWords } from './words';

/** Who has to do something: a person on site, Nexsoss remotely, or nobody
 *  yet (shown, not asked for). */
export type WhoActs = 'you' | 'nexsoss' | 'none';

/** Order inside "Needs you" (ideation §7): safety first, then blocked
 *  work, then upkeep. Cleaning gaps (the fourth band) are slice 2. */
export type Band = 'safety' | 'blocked' | 'upkeep';

export const BAND_RANK: Readonly<Record<Band, number>> = { safety: 0, blocked: 1, upkeep: 2 };

export type RobotCopyKey =
  | 'doorway_blocked'
  | 'emergency_stop'
  | 'clean_cancel_failed'
  | 'exit_blocked'
  | 'boarding_blocked'
  | 'unreachable'
  | 'dustbag'
  | 'fire_relocalize'
  | 'dock_failed'
  | 'localize_failed'
  | 'localize_fallback'
  | 'lift_release_refused'
  | 'lift_held_robot_out'
  | 'robot_error'
  | 'stuck_task'
  | 'not_localized'
  | 'state_channel_stale'
  | 'edge_offline'
  | 'not_reporting'
  | 'unmatched';

/** What a row is keyed on. `issue`/`issue_prefix` are Phantas categories
 *  (the prefix's remainder is the row's argument: a lift, a place, a
 *  station); `reason` is `RobotState.unavailable_reason`; `derived` rows
 *  come from the robot's status or its task. */
export type RobotCopyMatch =
  | { kind: 'issue'; category: string }
  | { kind: 'issue_prefix'; prefix: string }
  | { kind: 'reason'; reasons: readonly string[] }
  | { kind: 'derived' };

export interface RobotCopyContext {
  /** The robot's name. */
  robot: string;
  /** The prefix row's argument, or the raw category for `unmatched`. */
  arg: string | null;
  /** The robot's map name (`L9`), when its position is trusted. */
  level: string | null;
}

export interface RobotCopyRow {
  key: RobotCopyKey;
  match: RobotCopyMatch;
  whoActs: WhoActs;
  band: Band;
  /** Placed first in its group, whatever its stamp. */
  rankFirst?: true;
  /** Never shown to a site audience: no card, and toasts say only that
   *  Nexsoss needs to check it. */
  opsOnly?: true;
  /** The card's headline, in the hotel's words. */
  headline: (c: RobotCopyContext) => string;
  /** Numbered steps for the person on site. The e-stop's are dynamic and
   *  come from `estopCard` instead; these are its static fallback. */
  steps: (c: RobotCopyContext) => string[];
}

const onLevel = (level: string | null): string => {
  const words = levelWords(level);
  return words ? ` on ${words}` : '';
};

const arg = (c: RobotCopyContext, fallback: string): string => c.arg ?? fallback;

export const CALL_NEXSOSS = 'Call Nexsoss.';

/** What a site reader reads for a report they may not see the words of:
 *  an ops-only row on a toast, and a category this table does not know
 *  (the robot's raw text, often a code) on a card and a toast alike. */
export const NEEDS_A_CHECK = 'Needs a check by Nexsoss';

/** The robot panel's dust-bag button, named exactly as it reads
 *  (`DustbagConfirmAction.tsx`): a step must name a button that exists. */
export const DUSTBAG_BUTTON = 'Confirm bag emptied';

/** Every row, in no particular order: ordering is the selector's job. */
export const ROBOT_COPY: readonly RobotCopyRow[] = [
  {
    key: 'doorway_blocked',
    match: { kind: 'issue_prefix', prefix: 'DOORWAY_BLOCKED: ' },
    whoActs: 'you',
    band: 'safety',
    rankFirst: true,
    headline: (c) => `Standing in the ${arg(c, 'lift')} doorway, holding the car`,
    steps: (c) => [`Keep guests clear of the ${arg(c, 'lift')} doors.`, CALL_NEXSOSS],
  },
  {
    key: 'emergency_stop',
    match: { kind: 'issue', category: 'emergency_stop' },
    whoActs: 'you',
    band: 'safety',
    headline: () => 'Stopped: its red stop button is pressed',
    // Static fallback only; the card's steps are `estopCard`'s.
    steps: () => ['Go and look.', 'Call Nexsoss before you twist the red button.'],
  },
  {
    key: 'clean_cancel_failed',
    match: { kind: 'issue', category: 'the clean did not stop when it was cancelled' },
    whoActs: 'you',
    band: 'safety',
    headline: () => 'May still be cleaning after its job was cancelled',
    steps: () => ['Go and check whether it is still cleaning.', CALL_NEXSOSS],
  },
  {
    key: 'exit_blocked',
    match: { kind: 'issue_prefix', prefix: 'EXIT_BLOCKED: ' },
    whoActs: 'you',
    band: 'safety',
    headline: (c) => `Inside ${arg(c, 'the lift')} and can't get out${onLevel(c.level)}`,
    steps: (c) => [`Clear the ${arg(c, 'lift')} landing so it can get out.`, CALL_NEXSOSS],
  },
  {
    key: 'boarding_blocked',
    match: { kind: 'issue_prefix', prefix: 'BOARDING_BLOCKED: ' },
    whoActs: 'you',
    band: 'blocked',
    headline: (c) =>
      `Can't get into ${arg(c, 'the lift')}${onLevel(c.level)}: something is in the car`,
    steps: (c) => [
      `Ask guests to wait for the next car, or move the trolley out of ${arg(c, 'the lift')}.`,
    ],
  },
  {
    key: 'unreachable',
    match: { kind: 'issue_prefix', prefix: 'cannot reach ' },
    whoActs: 'you',
    band: 'blocked',
    headline: (c) => `Can't get to ${arg(c, 'where it is going')}`,
    steps: (c) => [
      `Check nothing is blocking the way near ${arg(c, 'where it is going')}.`,
      CALL_NEXSOSS,
    ],
  },
  {
    key: 'dustbag',
    match: { kind: 'issue_prefix', prefix: 'empty the dustbag at ' },
    whoActs: 'you',
    band: 'upkeep',
    headline: (c) => `Dust bag full at ${arg(c, 'its dust bag station')}`,
    steps: () => ['Empty its dust bag.', `Press ${DUSTBAG_BUTTON} in its robot panel.`],
  },
  {
    key: 'fire_relocalize',
    match: { kind: 'issue', category: 'confirm which floor this robot is on' },
    whoActs: 'you',
    band: 'blocked',
    headline: () => 'After the fire alarm: confirm which floor it is on',
    steps: (c) => fireConfirmSteps(c.level),
  },
  {
    key: 'dock_failed',
    match: { kind: 'issue', category: 'the robot did not dock onto its charger' },
    whoActs: 'nexsoss',
    band: 'upkeep',
    headline: () => "Couldn't plug into its charger",
    steps: () => ['Nexsoss may ask you to check its charger is clear.'],
  },
  {
    key: 'localize_failed',
    match: { kind: 'issue', category: 'localize_failed' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Lost its place on the map',
    steps: () => [],
  },
  {
    key: 'localize_fallback',
    match: { kind: 'issue', category: 'localize_fallback' },
    whoActs: 'nexsoss',
    band: 'blocked',
    opsOnly: true,
    headline: () => "A lift's starting point needs checking",
    steps: () => [],
  },
  {
    key: 'lift_release_refused',
    match: { kind: 'issue', category: 'lift claim not released' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Still holding a lift after being asked to let it go',
    steps: () => [],
  },
  {
    // In the Nexsoss group and ranked FIRST there (plan W5 step 6): the car
    // is held for a robot that is demonstrably not in it, and only a
    // console action (Recover) frees it. Nothing on site helps.
    key: 'lift_held_robot_out',
    match: { kind: 'issue', category: 'lift held with robot out' },
    whoActs: 'nexsoss',
    band: 'blocked',
    rankFirst: true,
    headline: () => "A lift is being held for it, but it isn't in the car",
    steps: () => ['Nothing for you to press.'],
  },
  {
    key: 'robot_error',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Reports an error',
    steps: () => [],
  },
  {
    key: 'stuck_task',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => "Its job isn't moving forward",
    steps: () => [],
  },
  {
    key: 'not_localized',
    match: { kind: 'reason', reasons: ['not_localized', 'pose_not_on_graph', 'location_status_absent'] },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Lost its place on the map',
    steps: () => [],
  },
  {
    // The one reason whose advice is "don't go": the robot is usually
    // healthy and driving, and only the platform's view of it is gone.
    // Said of the robot's updates, never as "Nexsoss has lost sight of
    // it": over an evidence line reading "Nexsoss may not know", that
    // claimed Nexsoss knew and did not know in one card.
    key: 'state_channel_stale',
    match: { kind: 'reason', reasons: ['state_channel_stale'] },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => "Its updates have stopped. It is probably fine; you don't need to go",
    steps: () => [],
  },
  {
    key: 'edge_offline',
    match: { kind: 'reason', reasons: ['edge_offline'] },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => 'Its site box stopped reporting',
    steps: () => [],
  },
  {
    key: 'not_reporting',
    match: { kind: 'reason', reasons: ['not_reporting'] },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: () => "The site box can't reach the robot (power or Wi-Fi)",
    steps: () => ['Nexsoss may ask you to check it near where it was last seen.'],
  },
  {
    key: 'unmatched',
    match: { kind: 'derived' },
    whoActs: 'nexsoss',
    band: 'blocked',
    headline: (c) => `Reports: "${c.arg ?? ''}"`,
    steps: () => [],
  },
];

const BY_KEY = new Map<RobotCopyKey, RobotCopyRow>(ROBOT_COPY.map((r) => [r.key, r]));

export function robotCopyRow(key: RobotCopyKey): RobotCopyRow {
  const row = BY_KEY.get(key);
  if (!row) throw new Error(`no robot copy row ${key}`);
  return row;
}

export interface RobotCopyMatchResult {
  row: RobotCopyRow;
  /** The prefix row's argument, or the raw category for `unmatched`. */
  arg: string | null;
}

/**
 * The row for one Phantas issue category, verbatim from `issues[]`.
 *
 * Exact categories first, then prefixes (with the remainder as the
 * argument). Matching is exact and case-sensitive, like the adapter's
 * contract with the worker; anything else is `unmatched`, carrying the raw
 * text.
 */
export function robotCopyForCategory(category: string): RobotCopyMatchResult {
  for (const row of ROBOT_COPY) {
    if (row.match.kind === 'issue' && row.match.category === category) return { row, arg: null };
  }
  for (const row of ROBOT_COPY) {
    if (
      row.match.kind === 'issue_prefix' &&
      category.startsWith(row.match.prefix) &&
      category.length > row.match.prefix.length
    ) {
      return { row, arg: category.slice(row.match.prefix.length) };
    }
  }
  return { row: robotCopyRow('unmatched'), arg: category };
}

/** The row for an `unavailable_reason`, or null for a reason the table
 *  does not word (the robot's status then speaks for itself). */
export function robotCopyForReason(reason: string | null | undefined): RobotCopyRow | null {
  if (!reason) return null;
  for (const row of ROBOT_COPY) {
    if (row.match.kind === 'reason' && row.match.reasons.includes(reason)) return row;
  }
  return null;
}

/**
 * "Confirm which floor" (plan W5 step 6). Confirm floor and release
 * resumes the task and does NOT relocalise (`FireRelocalizeAction.tsx`),
 * so the step is conditional on the robot really being where the map
 * says, and it warns that the robot will move.
 */
export function fireConfirmSteps(level: string | null): string[] {
  const words = levelWords(level);
  if (!words) return ["Its floor isn't known. Call Nexsoss."];
  return [
    `Only if it is standing on ${words}: check its path is clear, then press Confirm floor and release in its panel. It will start moving to resume its job.`,
    'Otherwise call Nexsoss.',
  ];
}
